import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Users, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ArchivePage = ({ sessionId }) => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchArchive = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/topics/archive`);
      setTopics(response.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('[Vent:archive]', err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArchive();
  }, [fetchArchive]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-2 border-vent-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vent-bg bg-pattern">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-40 glass">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="archive-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-zinc-400" />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">Archive</h1>
        </div>
      </nav>

      <main className="pt-24 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Intro */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <Calendar className="w-12 h-12 mx-auto mb-4 text-vent-primary" />
            <h2 className="font-heading text-2xl font-bold text-white mb-2">
              Past Debates
            </h2>
            <p className="text-zinc-500">
              Explore previous topics and see how the community voted
            </p>
          </motion.div>

          {/* Topics List */}
          {topics.length > 0 ? (
            <div className="space-y-4">
              {topics.map((topic, index) => (
                <motion.button
                  key={topic.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => navigate(`/results/${topic.id}`)}
                  className="w-full glass rounded-2xl p-6 text-left hover:bg-white/5 transition-colors group"
                  data-testid={`archive-topic-${topic.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs tracking-wider uppercase text-zinc-600">
                          {formatDate(topic.date)}
                        </span>
                        <div className="flex items-center gap-1 text-zinc-500 text-xs">
                          <Users className="w-3 h-3" />
                          <span>{topic.votes_for + topic.votes_against} votes</span>
                        </div>
                      </div>
                      <h3 className="font-heading text-xl font-bold text-white group-hover:text-vent-primary transition-colors">
                        {topic.title}
                      </h3>
                      <p className="text-zinc-500 text-sm mt-1 line-clamp-2">
                        {topic.description}
                      </p>

                      {/* Mini result bar */}
                      <div className="mt-4 flex items-center gap-4">
                        <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden flex">
                          <div
                            className="bg-vent-for"
                            style={{
                              width: `${topic.votes_for + topic.votes_against > 0
                                ? (topic.votes_for / (topic.votes_for + topic.votes_against)) * 100
                                : 50}%`
                            }}
                          />
                          <div
                            className="bg-vent-against"
                            style={{
                              width: `${topic.votes_for + topic.votes_against > 0
                                ? (topic.votes_against / (topic.votes_for + topic.votes_against)) * 100
                                : 50}%`
                            }}
                          />
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-vent-for">{topic.votes_for}</span>
                          <span className="text-zinc-600">/</span>
                          <span className="text-vent-against">{topic.votes_against}</span>
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-vent-primary transition-colors ml-4" />
                  </div>
                </motion.button>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-zinc-900 flex items-center justify-center">
                <Calendar className="w-10 h-10 text-zinc-700" />
              </div>
              <h3 className="font-heading text-xl text-zinc-400 mb-2">No Past Debates Yet</h3>
              <p className="text-zinc-600 max-w-md mx-auto">
                Come back tomorrow to see today's debate in the archive. 
                The Universe presents a new topic every day!
              </p>
              <button
                onClick={() => navigate('/')}
                className="mt-6 px-6 py-3 bg-vent-primary/20 text-vent-primary rounded-full font-bold hover:bg-vent-primary/30 transition-colors"
                data-testid="go-to-today-btn"
              >
                Today's Debate
              </button>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ArchivePage;
